import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "../../data/content";
import { Logo } from "../Logo";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "border-b border-border/80 bg-white/80 backdrop-blur-xl"
          : "border-b border-transparent bg-white/40 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <Logo />

        <nav className="hidden items-center gap-0.5 xl:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-md px-2.5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-brand"
                    : "text-steel hover:text-brand"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <Link
            to="/login"
            data-testid="header-login"
            className="rounded-md px-3 py-2 text-sm font-semibold text-brand transition-colors hover:text-brand-700"
          >
            Client Login
          </Link>
          <Link
            to="/quote"
            data-testid="header-quote-cta"
            className="inline-flex items-center gap-1.5 rounded-full bg-aero px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-aero-600"
          >
            Request a Quote <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-navy xl:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="border-t border-border bg-white xl:hidden">
          <div className="mx-auto max-w-7xl px-5 py-4">
            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `rounded-md px-3 py-3 text-base font-medium transition-colors ${
                      isActive ? "bg-secondary text-brand" : "text-steel"
                    }`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/login"
                data-testid="mobile-login"
                className="mt-2 rounded-md px-3 py-3 text-base font-semibold text-brand"
              >
                Client Login
              </Link>
              <Link
                to="/quote"
                data-testid="mobile-quote-cta"
                className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-aero px-5 py-3 text-sm font-semibold text-white"
              >
                Request a Quote <ArrowUpRight className="h-4 w-4" />
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};
